import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { withPrismicPreview } from 'gatsby-plugin-prismic-previews';

import NotFoundPage from '../components/404';
import PageData from '../helpers/pageData';
import Layout from '../components/Layouts/Layout';
import Body from '../components/ui/Body/Body';
import getPageContextFromData from '../lib/dto/getPageContextFromData';
import StructuredData from '../helpers/StructuredData';

const Report = ({
  data: { prismicReport: { type, uid, data } },
}) => {
  if (!data) return <NotFoundPage />;

  const dataContext = getPageContextFromData(type, uid, data);

  return (
    <PageData.Provider value={dataContext}>
      <StructuredData
        type="Article"
        title={data.seo_title || data.title}
        description={data.seo_description || data.page_short_description}
        pathname={dataContext.url}
        image={data.landscape_image || data.thumbnail_image}
        author="Portable"
      />
      <Layout>
        <Body />
      </Layout>
    </PageData.Provider>
  );
};

export const query = graphql`
query ReportPostBySlug($uid: StringQueryOperatorInput = {}) {
  prismicReport(uid: $uid) {
    _previewable
    uid
    type
    data {
      introduction
      page_colour
      page_short_description
      prismic_page_name
      seo_description
      seo_title
      title
      thumbnail_image {
        localFile {
          publicURL
          extension
          childImageSharp {
            gatsbyImageData(
              layout: CONSTRAINED,
              quality: 100,
              width: 640,
              breakpoints: [640, 960, 1400]
            )
          }
        }
      }
      landscape_image {
        localFile {
          publicURL
          extension
          childImageSharp {
            gatsbyImageData(
              layout: CONSTRAINED,
              quality: 100,
              width: 1400,
              breakpoints: [640, 960, 1400]
            )
          }
        }
      }
      body {
        ... on PrismicReportDataBodyContent {
          id
          slice_type
          primary {
            content_heading {
              text
            }
            content_subtitle {
              richText
              text
            }
            content_text {
              richText
              text
            }
            layout
          }
        }
        ... on PrismicReportDataBodyImage {
          id
          slice_type
          primary {
            alignment
            image {
              localFile {
                publicURL
                extension
                childImageSharp {
                  gatsbyImageData(
                    layout: CONSTRAINED,
                    quality: 100,
                    width: 1400,
                    breakpoints: [640, 960, 1400]
                  )
                }
              }
            }
          }
        }
        ... on PrismicReportDataBodyVideo {
          id
          slice_type
          primary {
            alignment
            video_embeded {
              embed_url
            }
          }
        }
        ... on PrismicReportDataBodyQuote {
          id
          slice_type
          primary {
            quote {
              richText
              text
            }
          }
        }
        ... on PrismicReportDataBodyBanner {
          id
          slice_type
          primary {
            background_colour
            text_colour
            big_text {
              richText
              text
            }
            text_link {
              richText
              text
            }
          }
        }
        ... on PrismicReportDataBodyRelatedContent {
          id
          slice_type
          primary {
            items_to_show
            related_content_title {
              text
            }
          }
        }
        ... on PrismicReportDataBodyDownloadFormHs {
          id
          slice_type
          primary {
            form_title
            hubspot_form_id
            form_image {
              localFile {
                publicURL
                extension
                childImageSharp {
                  gatsbyImageData(
                    layout: CONSTRAINED,
                    quality: 100,
                    width: 1400,
                    breakpoints: [640, 960, 1400]
                  )
                }
              }
            }
          }
        }
        ... on PrismicReportDataBodyActionButton {
          id
          slice_type
          primary {
            layout
            button_text {
              text
            }
            button_url {
              id
              link_type
              isBroken
              type
              uid
              url
              target
            }
          }
        }
      }
      process_tags {
        processes {
          slug
          type
        }
      }
      purpose_tags {
        purposes {
          type
          slug
        }
      }
      service_tags {
        services {
          type
          slug
        }
      }
    }
  }
}
`;

Report.propTypes = {
  data: PropTypes.shape({
    prismicReport: PropTypes.object.isRequired,
  }).isRequired,
};

export default withPrismicPreview(Report);
